/**
 * غلاف يتحكّم بشفافيّة خلفيّة بلوك في الصفحة الرئيسيّة.
 * عند قيمة أقلّ من ١٠٠٪ يُبطِل خلفيّة البلوك الداخليّة ويرسم بدلاً منها
 * طبقة خلفيّة بنسبة العتامة المطلوبة، فتظهر خلفيّة الصفحة تحتها.
 *
 * يدعم أيضاً «معاينة حيّة» من لوحة الأدمن عبر postMessage:
 *  - { type: "lecmo:block-opacity-preview", opacity: { [key]: { mobile, desktop } } }
 *  - { type: "lecmo:scroll-to-block", key: HomeBlockKey }
 */
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useIsMobile } from "@/hooks/use-mobile";
import { getHomeBlockOpacity } from "@/utils/homeBlockOpacity.functions";
import type { HomeBlockKey } from "@/lib/home-block-keys";

export const BLOCK_OPACITY_PREVIEW_MSG = "lecmo:block-opacity-preview";
export const BLOCK_SCROLL_MSG = "lecmo:scroll-to-block";

export type PreviewOpacity = Record<string, { mobile?: number; desktop?: number } | number>;

export function useHomeBlockOpacity() {
  const fetchFn = useServerFn(getHomeBlockOpacity);
  return useQuery({
    queryKey: ["home-block-opacity"],
    queryFn: () => fetchFn({ data: {} }),
    staleTime: 60_000,
  });
}

export function HomeBlockOpacityWrap({
  blockKey,
  children,
}: {
  blockKey: HomeBlockKey;
  children: React.ReactNode;
}) {
  const isMobile = useIsMobile();
  const { data } = useHomeBlockOpacity();
  const hostRef = React.useRef<HTMLDivElement>(null);
  const [preview, setPreview] = React.useState<{ mobile?: number; desktop?: number } | null>(null);

  React.useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const msg = e.data as {
        type?: string;
        opacity?: PreviewOpacity;
        key?: string;
      } | null;
      if (!msg || typeof msg !== "object") return;
      if (msg.type === BLOCK_OPACITY_PREVIEW_MSG && msg.opacity) {
        const v = msg.opacity[blockKey];
        if (typeof v === "number") {
          setPreview({ mobile: v, desktop: v });
        } else if (v && typeof v === "object") {
          setPreview({ mobile: v.mobile, desktop: v.desktop });
        } else {
          setPreview(null);
        }
      }
      if (msg.type === BLOCK_SCROLL_MSG && msg.key === blockKey) {
        hostRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [blockKey]);

  const stored = data?.opacity?.[blockKey];
  const deviceValue = isMobile
    ? preview?.mobile ?? stored?.mobile
    : preview?.desktop ?? stored?.desktop;
  const value = typeof deviceValue === "number" ? deviceValue : 100;
  const transparent = value < 100;

  // لون خلفيّة البلوك المخصَّص (إن حدّده الأدمن) — يُقرأ من العنصر الابن
  // حتى لا تتحوّل الشفافيّة إلى أبيض عند تخفيف السواد.
  const [blockBg, setBlockBg] = React.useState<string | null>(null);
  React.useEffect(() => {
    const el = hostRef.current?.querySelector<HTMLElement>("[data-block-bg]");
    const color = el?.getAttribute("data-block-bg")?.trim() || null;
    setBlockBg(color || null);
  }, [children]);

  const baseColor = blockBg || "var(--background)";

  return (
    <div
      ref={hostRef}
      data-home-block={blockKey}
      data-block-opacity={value}
      data-device={isMobile ? "mobile" : "desktop"}
      className={
        transparent
          ? "relative [&>*]:!bg-transparent [&>*]:!backdrop-blur-none [&_img]:opacity-[var(--block-op)] [&_video]:opacity-[var(--block-op)]"
          : "relative"
      }
      style={
        transparent
          ? ({
              backgroundColor: `color-mix(in srgb, ${baseColor} ${value}%, transparent)`,
              backdropFilter: value > 0 ? "blur(2px)" : undefined,
              "--block-op": String(value / 100),
            } as React.CSSProperties)
          : // ١٠٠٪ = تعتيم كامل: نرسم خلفيّة معتمة فعليّاً حتى تُحجب خلفيّة الصفحة
            ({ backgroundColor: baseColor } as React.CSSProperties)
      }
    >

      {children}
    </div>
  );
}
